import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';

import { TripPlannerPax } from '@apps/main/modules/recipe/pax';
import type { TripPlannerRoomPax } from '@apps/main/modules/recipe/pax';

import type { Pax } from '@libs/trip-planner-interfaces';

import type { CoreEventSource } from '../../models';
import type { RoomPaxConfig } from '../../../modules/paxSelector';
import { useBrowserStore } from '../browserStore';
import { useRecipeStore } from '../recipe/recipeStore';
import { useStatsigEventsStore } from '../statsigEvents/statsigEventsStore';

import {
  getFormattedChildrenAges,
  getFormattedPax,
  savePaxInKeyValueStorage,
} from './paxStoreLogic';
import type { PaxMutation } from './paxStore.model';
import { PaxMutationType } from './paxStore.model';

export const usePaxStore = defineStore('PaxStore', () => {
  const browserStore = useBrowserStore();
  const recipeStore = useRecipeStore();
  const statsigEventsStore = useStatsigEventsStore();

  // State
  const pax = ref<TripPlannerPax>(
    new TripPlannerPax([{ numberOfAdults: 2, childAges: [] }])
  );
  const shouldShowNumberOfVehiclesUpdatedWarning = ref<boolean>(false);

  //Mutations
  function mutatePax(mutation: PaxMutation): void {
    switch (mutation.type) {
      case PaxMutationType.UpdatePax: {
        handleUpdatePax(mutation.newPax, mutation.source);
        break;
      }
      case PaxMutationType.ClearWarningMessages: {
        shouldShowNumberOfVehiclesUpdatedWarning.value = false;
        break;
      }
      default: {
        const exhaustiveCheck: never = mutation;
        throw new Error(
          `Unhandled mutation type: ${JSON.stringify(exhaustiveCheck)}`
        );
      }
    }
  }

  function handleUpdatePax(newPax: Pax, source?: CoreEventSource): void {
    const updatedPax = new TripPlannerPax(newPax);
    pax.value = updatedPax;

    if (source) {
      shouldShowNumberOfVehiclesUpdatedWarning.value =
        recipeStore.shouldShowNumberOfVehiclesUpdatedWarning(
          updatedPax.totalNumberOfAdults
        );
      recipeStore.removeAllSelectedRooms();
      recipeStore.updateNumberOfFerryVehiclesBaseOnAdults(
        updatedPax.totalNumberOfAdults
      );
      statsigEventsStore.logCorePaxUpdated(recipeStore.anyRoomSelected, {
        source,
      });
      savePaxInKeyValueStorage(
        updatedPax.rooms,
        browserStore.setSessionStorageItem
      );
    }
  }

  // Actions
  function updatePax(newPax: Pax, source?: CoreEventSource): void {
    mutatePax({
      type: PaxMutationType.UpdatePax,
      newPax,
      source,
    });
  }

  function clearWarningMessages(): void {
    mutatePax({
      type: PaxMutationType.ClearWarningMessages,
    });
  }

  // Getters
  const paxesChildrenAgesText = computed<string | undefined>(() =>
    getFormattedChildrenAges(pax.value.rooms)
  );

  const getPaxesButtonSimpleText = computed<string>(() =>
    getFormattedPax(pax.value.rooms, false)
  );
  const getPaxesButtonDetailedText = computed<string>(() =>
    getFormattedPax(pax.value.rooms, true)
  );

  const shouldShowTicketsWarning = computed<boolean>(
    () => recipeStore.doesAnyStayHaveSubingredients
  );

  const shouldShowRoomSelectionWarning = computed<boolean>(
    () => recipeStore.anyRoomSelected
  );

  const initialPaxes = computed<RoomPaxConfig[]>(() =>
    pax.value.tripPlannerRooms.map((room: TripPlannerRoomPax) => ({
      amountOfAdults: room.numberOfAdultsInRoom,
      amountOfChildren: room.numberOfChildrenInRoom,
      childAges: room.childAgesInRoom,
    }))
  );

  const numberOfAdults = computed<number>(() =>
    initialPaxes.value.reduce(
      (acc: number, room: RoomPaxConfig) => acc + room.amountOfAdults,
      0
    )
  );

  const numberOfInfants = computed<number>(() =>
    initialPaxes.value.reduce(
      (acc: number, room: RoomPaxConfig) =>
        acc + room.childAges.filter((age) => age < 2).length,
      0
    )
  );

  const numberOfTravelers = computed<number>(() =>
    initialPaxes.value.reduce(
      (acc: number, room: RoomPaxConfig) =>
        acc + room.amountOfAdults + room.amountOfChildren,
      0
    )
  );

  const shouldShowNumberOfVehiclesWillBeUpdatedWarning = computed<boolean>(() =>
    recipeStore.ferryOnlyRecipe.some((ferry) => ferry.vehicles.length > 1)
  );

  return {
    // State
    pax: readonly(pax),
    shouldShowNumberOfVehiclesUpdatedWarning: readonly(
      shouldShowNumberOfVehiclesUpdatedWarning
    ),

    // Actions
    updatePax,
    clearWarningMessages,

    // Getters
    initialPaxes,
    numberOfAdults,
    numberOfInfants,
    numberOfTravelers,
    paxesChildrenAgesText,
    getPaxesButtonSimpleText,
    getPaxesButtonDetailedText,
    shouldShowTicketsWarning,
    shouldShowRoomSelectionWarning,
    shouldShowNumberOfVehiclesWillBeUpdatedWarning,
  };
});
